// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';
import { trackClicked } from 'utils/analytics';
import { algoliaTrackClick } from 'utils/algoliaSearch';
import { scrollToTop } from 'utils/scroll';
import type { CurrentUser } from 'api/currentUser';
import SearchHit from 'components/SearchHit';
import Pagination from 'components/Pagination';
import Button from 'components/Button';
import Divider from 'components/Divider';
import LoadingIndicator from 'components/LoadingIndicator';
import SearchEmptyResultsMessage from 'components/SearchEmptyResultsMessage';
import SearchFiltersSidebarContainer from 'containers/VolopsSearchFiltersSidebarContainer';
import SaveListingContainer from 'containers/SaveListingContainer';
import Box from 'components/Box';
import Hide from 'components/Hide';
import Flex from 'components/Flex';
import Text, { BodySmall } from 'components/Text';
import EmailAlertButton from 'components/EmailAlertButton';
import SortMenu from 'components/SortMenu';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import {
  OBJECT_TYPE_PLURAL_HUMAN_NAME_MAP,
  ACTION_TYPE_PLURAL
} from 'utils/constants';
import { gutterWidth, colors, transition } from '../theme';

type Props = {
  clearAllFilters: Function,
  selectSearchFilterOption: Function,
  selectAllSearchFilterOptions: Function,
  deselectSearchFilterOption: Function,
  setSearchFilter: Function,
  mainSearch: MainSearch,
  clearSearchLocation: Function,
  scrollToId: string,
  searchLocation: SearchLocation,
  currentSearchIsSaved: boolean,
  saveableSearch: boolean,
  saveSearch: () => mixed,
  currentUser: CurrentUser
};

const FilterLink = styled(({ active, children, ...props }) => (
  <Button {...props}>
    <BodySmall lineHeight={1.2} styleAs={active ? 'h5' : 'bodySmall'}>
      {children}
    </BodySmall>
  </Button>
))`
  margin-right: 16px;
  transition: all ${transition};
  ${props =>
    props.active
      ? `
    color: ${colors.waterBlue};
    text-decoration: underline;
  `
      : `
    color: ${colors.battleshipGrey};
  `}
  &:hover, &:focus {
    color: ${colors.waterBlue};
  }
`;

const ResultCount = ({ nbHits }: { nbHits: ?number }) => (
  <Text
    display={['block', 'block', 'block', 'inline-block']}
    textAlign={['center', 'center', 'center', 'left']}
    mb={['20px', '20px', '20px', 0]}
    fontSize={['20px', null, null, '16px']}
  >
    {typeof nbHits === 'number' &&
      (nbHits === 1
        ? /* TRANSLATORS: Grad School Home Page - search results description */

          getText('1 opportunity found')
        : /* TRANSLATORS: Grad School Home Page - search results description */

          sprintf(getText('%(resultCount)s opportunities found'), {
            resultCount: nbHits.toLocaleString(USER_LOCALE)
          }))}
  </Text>
);

class VolopsSearchResults extends React.PureComponent<Props> {
  resultsRef = React.createRef<Element>();

  selectFilter(id: string) {
    if (id === 'NONE') {
      this.props.selectAllSearchFilterOptions('actionTypeFacet');
    } else {
      this.props.setSearchFilter('actionTypeFacet', [id]);
    }
  }

  onPageClick = () => scrollToTop();

  render() {
    const {
      clearAllFilters,
      mainSearch,
      clearSearchLocation,
      scrollToId,
      searchLocation,
      currentSearchIsSaved,
      saveableSearch,
      saveSearch,
      currentUser
    } = this.props;
    const {
      pageIndex,
      resultsByPage,
      queryID,
      isSearching,
      initialized,
      query,
      filters,
      searchFacets
    } = mainSearch;
    const results = resultsByPage[pageIndex.toString()];
    const { hits, nbHits, hitsPerPage, page, nbPages } = results || {};

    const allFiltersSelected =
      filters.actionTypeFacet instanceof Array &&
      filters.actionTypeFacet.length ===
        searchFacets.actionTypeFacet.options.length;

    const filterLinks = ACTION_TYPE_PLURAL.map(action => (
      <FilterLink
        key={action.id}
        active={
          action.id === 'NONE'
            ? filters.actionTypeFacet === 'ALL' || allFiltersSelected
            : filters.actionTypeFacet instanceof Array &&
              filters.actionTypeFacet.includes(action.id) &&
              !allFiltersSelected
        }
        onClick={() => this.selectFilter(action.id)}
        data-qa-id={`action-filter-link-${action.name}`}
      >
        {action.name}
      </FilterLink>
    ));

    const SearchResultsBody = () =>
      hits && hits.length > 0 && queryID ? (
        <>
          {results.hits.map((hit, index) => (
            <React.Fragment key={hit.objectID}>
              <SaveListingContainer
                listingId={hit.objectID}
                listingType={hit.type}
                listingName={hit.name}
                orgId={hit.orgID || null}
                orgType={hit.orgType || null}
                render={({ saveListing, unsaveListing, saved, canSave }) => (
                  <SearchHit
                    canSave={
                      canSave &&
                      (hit.type === 'VOLOP' ||
                        hit.type === 'IDEALIST_DAY_EVENT')
                    }
                    saved={saved}
                    saveListing={saveListing}
                    unsaveListing={unsaveListing}
                    hit={hit}
                    currentUser={currentUser}
                    showLabel
                    scrollIntoView={hit.objectID === scrollToId}
                    applications={
                      currentUser && currentUser.id
                        ? currentUser.listingApplications.map(({ id }) => id)
                        : []
                    }
                    trackClick={() => {
                      algoliaTrackClick(
                        queryID,
                        hit.objectID,
                        // Position in algolia analytics is 1-indexed
                        hitsPerPage ? page * hitsPerPage + index + 1 : index + 1
                      );
                      trackClicked('Search Result', {
                        org_id: hit.orgID,
                        org_type: hit.orgType,
                        result_id: hit.objectID,
                        result_index: index,
                        result_type: hit.type,
                        result_source: hit.source || 'IDEALIST',
                        action_type: hit.actionType || undefined,
                        action_sub_type: hit.actionSubType || undefined
                        // TODO: add tracking data
                        // ...getSearchTrackingData()
                      });
                    }}
                  />
                )}
              />
            </React.Fragment>
          ))}
          {typeof nbPages === 'number' &&
            typeof page === 'number' &&
            typeof hitsPerPage === 'number' && (
              <Pagination
                nbHits={nbHits}
                nbPages={nbPages}
                pageIndex={page}
                hitsPerPage={hitsPerPage}
                onClick={this.onPageClick}
              />
            )}
        </>
      ) : isSearching || !initialized ? (
        <LoadingIndicator />
      ) : (
        <SearchEmptyResultsMessage
          allowSaveSearch
          query={query}
          searchLocation={searchLocation}
          useLocation={mainSearch.useLocation}
          clearSearchLocation={clearSearchLocation}
          clearAllFilters={clearAllFilters}
          resultType={OBJECT_TYPE_PLURAL_HUMAN_NAME_MAP.VOLOP}
        />
      );

    return (
      <Box id="results" mx="auto" display="flex" flexWrap="wrap">
        <Box width={[1, 1, 1, 1 / 3]} px={[0, null, null, gutterWidth]}>
          <Hide lg xl>
            <BodySmall lineHeight={1.25}>
              <ResultCount nbHits={nbHits} />
              {query && (
                <Box
                  mt={12}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box ml={15}>{getText('Sort by')}</Box>
                  <SortMenu
                    options={[
                      {
                        name: getText('Best Match'),
                        param: 'relevance'
                      },
                      {
                        name: getText('Newest'),
                        param: 'newest'
                      }
                    ]}
                  />
                </Box>
              )}
            </BodySmall>
            <Flex
              mt="11px"
              mb="31px"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="center"
            >
              {saveableSearch && (
                <EmailAlertButton
                  key={queryID}
                  primary={
                    (typeof nbHits !== 'number' || nbHits === 0) &&
                    !currentSearchIsSaved
                  }
                  m="5px"
                  onClick={e => {
                    e.stopPropagation();
                    saveSearch();
                    trackClicked('Get Email Alerts');
                  }}
                  saved={currentSearchIsSaved}
                />
              )}
            </Flex>
          </Hide>
          <SearchFiltersSidebarContainer />
        </Box>
        <Box
          width={[1, 1, 1, 2 / 3]}
          px={[gutterWidth / 2, null, null, gutterWidth]}
          data-qa-id="search-results"
        >
          <Hide xs sm md>
            <BodySmall mb={0}>
              <Box
                justifyContent="space-between"
                display="flex"
                alignItems="center"
              >
                <Box display="flex" alignItems="center" flex="0 0 auto">
                  <ResultCount nbHits={nbHits} />
                  <Box ml={24}>
                    <SortMenu
                      options={[
                        {
                          name: getText('Best Match'),
                          param: 'relevance'
                        },
                        {
                          name: getText('Newest'),
                          param: 'newest'
                        }
                      ]}
                    />
                  </Box>
                </Box>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  pl={[0, null, null, 16]}
                >
                  {saveableSearch && (
                    <EmailAlertButton
                      key={queryID}
                      primary={
                        (typeof nbHits !== 'number' || nbHits === 0) &&
                        !currentSearchIsSaved
                      }
                      onClick={e => {
                        e.stopPropagation();
                        saveSearch();
                        trackClicked('Get Email Alerts');
                      }}
                      data-qa-id={!currentSearchIsSaved && 'save-search'}
                      saved={currentSearchIsSaved}
                    />
                  )}
                </Box>
              </Box>
              <Divider light mt={12} mb={32} />
              <Box display="flex" my={32} alignItems="center">
                {filterLinks}
              </Box>
            </BodySmall>
          </Hide>
          <SearchResultsBody />
        </Box>
      </Box>
    );
  }
}

export default VolopsSearchResults;
